import ReactSlider from "react-slider";
import { MinusIcon } from "@heroicons/react/24/outline";
import { CheckIcon } from "@heroicons/react/24/solid";
import { TIERS, TIER_KEYS } from "../utils/constants";
import { classNames, formatNumber } from "../utils/formatters";
import { useState } from "react";
import { useRouter } from "next/router";
import { getBundleDiscountPercent, getBundlePrice } from "../utils/bundles";
import TierBadge, { TIER_COLORS, TIER_COLORS_HOVER } from "./TierBadge";
import {
  useApplyPurchaseMutation,
  useGetUserPurchasesQuery,
} from "../graphql/generated/graphql";
import { splitPurchasesPerType } from "../utils/purchases";
import { GET_USER_PURCHASES } from "../graphql/queries/purchase";
import Spinner from "./Spinner";
import { useDiscount } from "../context/DiscountsContext";

type TProps = {
  isPromote?: boolean;
  hideFree?: boolean;
  bundle?: boolean;
  vehicleId?: string;
  vehicleSlug?: string;
  vehicleTier?: string;
};

export default function PricingTable({
  isPromote,
  hideFree,
  bundle,
  vehicleId,
  vehicleSlug,
  vehicleTier,
}: TProps) {
  const { discountInfo } = useDiscount();
  const router = useRouter();

  const [multiplier, setMultiplier] = useState({
    [TIERS.free.id]: 1,
    // [TIERS.plus.id]: 5,
    [TIERS.max.id]: 5,
  });

  const { data } = useGetUserPurchasesQuery();
  const { getUserPurchases } = data || {};

  const purchasesPerType = splitPurchasesPerType(getUserPurchases || []);

  const [applyPurchase, { loading: applyingPurchase }] =
    useApplyPurchaseMutation({
      onCompleted: ({ applyPurchase }) => {
        if (applyPurchase) {
          router.push(`/autosusados/${vehicleSlug}`);
        }
      },
      refetchQueries: [{ query: GET_USER_PURCHASES }],
    });

  function changeMultiplier(id, value) {
    setMultiplier({
      ...multiplier,
      [id]: value || 1,
    });
  }

  function renderBundlePrice(id, price) {
    const multiplierDiscounts = TIERS[id].bundleDiscounts;

    return (
      <span>
        {`${multiplier[id]} ${
          multiplier[id] === 1 ? "publicación" : "publicaciones"
        } ${TIERS[id].name}`}
        <br />
        <span>
          por{" "}
          <span className="line-through text-red-500 text-3xl">{`₡${formatNumber(
            price * multiplier[id]
          )}`}</span>{" "}
          <span className=" text-emerald-500">{`₡${formatNumber(
            getBundlePrice(id, price, multiplier[id])
          )}`}</span>
        </span>
        <br />
        <span className="text-lg font-medium">
          <span className="line-through text-red-500">{`₡${price}`}</span>{" "}
          {`₡${formatNumber(
            price - price * multiplierDiscounts[multiplier[id].toString()]
          )}`}{" "}
          por publicación ({getBundleDiscountPercent(id, multiplier[id])}% de
          descuento)
        </span>
      </span>
    );
  }

  function getSinglePurchasePrice(price: number, discount: number) {
    const discountedPrice = discount ? price - price * (discount / 100) : price;

    return formatNumber(discountedPrice);
  }

  function goToAdd() {
    router.push("/vender");
  }

  function goToCheckout(e) {
    const tier = e.target.id;
    const query = {
      purchase: bundle ? `${tier}_${multiplier[tier]}` : tier,
      vehicleId: "",
    };

    if (vehicleId) {
      query.vehicleId = vehicleId;
    }

    router.push({
      pathname: `/checkout`,
      query,
    });
  }

  function onClick(e: React.MouseEvent<HTMLElement>) {
    if (bundle || isPromote) {
      goToCheckout(e);
    } else {
      goToAdd();
    }
  }

  async function usePurchase(e) {
    const type = e.target.id.split("use-")[1];
    const purchaseId = purchasesPerType[type][0];

    await applyPurchase({
      variables: {
        _id: purchaseId._id,
        resourceId: vehicleId,
      },
    });
  }

  let btnLabel = "Publicar auto";
  if (isPromote) {
    btnLabel = "Destacar";
  } else if (bundle) {
    btnLabel = "Comprar";
  }

  return (
    <div
      className={classNames(
        "space-y-4 sm:space-y-0 sm:grid grid-cols-1 sm:gap-4  mx-auto",
        // hideFree ? "sm:grid-cols-2" : "sm:grid-cols-3"
        bundle
          ? "sm:grid-cols-1 lg:max-w-2xl"
          : `${hideFree ? "sm:grid-cols-1" : "sm:grid-cols-2"} lg:max-w-4xl`
      )}
    >
      {Object.values(TIERS)
        .filter((tier) => (hideFree ? tier.id !== "free" : true))
        .filter((tier) => tier.id !== "collection")
        .map((tier) => {
          const disabledBtn =
            applyingPurchase ||
            vehicleTier === tier.id ||
            vehicleTier === TIER_KEYS.MAX;
          // && tier.id === TIER_KEYS.PLUS);
          return (
            <div
              key={tier.name}
              className={classNames(
                "rounded-lg divide-y divide-gray-200",
                // tier.id === selectedTier
                // ? "shadow-lg border-2 border-orange-500" :
                "shadow-sm border border-gray-200"
              )}
            >
              <div className="p-6">
                <div className="flex gap-x-2">
                  <h2 className="text-lg leading-6 font-medium text-gray-900">
                    {tier.name}
                  </h2>
                  <TierBadge tier={tier.id} />
                </div>
                <p className="mt-3 text-sm text-gray-500">{tier.description}</p>
                {bundle ? (
                  <p className="mt-3 font-medium text-gray-900">
                    Mové el slider para armar tu paquete de publicaciones{" "}
                    {tier.name}
                  </p>
                ) : null}
                {bundle ? (
                  <div className="py-6">
                    <ReactSlider
                      className="flex items-center"
                      thumbClassName="rounded-full bg-white shadow-sm border-2 border-orange-500 h-8 w-8"
                      thumbActiveClassName="outline-orange-500"
                      trackClassName="bg-gray-200 h-1"
                      defaultValue={5}
                      step={5}
                      min={5}
                      max={25}
                      // renderThumb={(props, state) => (
                      //   <div {...props}>{state.valueNow}</div>
                      // )}
                      onChange={(v) => changeMultiplier(tier.id, v)}
                    />
                  </div>
                ) : null}
                {vehicleTier === tier.id || vehicleTier === TIER_KEYS.MAX ? (
                  <div>
                    <p className="mt-6">
                      <span className="text-md font-medium text-gray-900">
                        {vehicleTier === tier.id &&
                        vehicleTier !== TIER_KEYS.MAX &&
                        vehicleTier !== TIER_KEYS.FREE
                          ? "Tu publicación ya posee este nivel de destacado, podés promover a MAX para obtener más beneficios."
                          : null}
                        {vehicleTier === TIER_KEYS.MAX
                          ? "Tu publicación ya posee el destacado MAX"
                          : null}
                      </span>
                    </p>
                  </div>
                ) : (
                  <div>
                    {purchasesPerType[tier.id]?.length && vehicleId ? (
                      <div>
                        <p className="mt-6">
                          <span className="text-md font-medium text-gray-900">
                            {`¡Tenés ${purchasesPerType[tier.id]?.length} ${
                              purchasesPerType[tier.id]?.length !== 1
                                ? "disponibles"
                                : "disponible"
                            } para usar!`}
                          </span>
                        </p>
                        <button
                          id={`use-${tier.id}`}
                          className={classNames(
                            "mt-3 inline-flex gap-x-1 justify-center w-full  border rounded-md py-2 text-sm font-semibold text-white text-center disabled:opacity-70",
                            TIER_COLORS[tier.id],
                            !disabledBtn ? TIER_COLORS_HOVER[tier.id] : ""
                          )}
                          onClick={usePurchase}
                          disabled={disabledBtn}
                        >
                          {applyingPurchase ? (
                            <Spinner className="h-5 w-5" />
                          ) : null}
                          Usar
                        </button>
                        <p className="mt-3">
                          <span className="text-md font-medium text-gray-900">
                            O comprá por
                          </span>
                        </p>
                      </div>
                    ) : null}
                    <p className="mt-6">
                      <span
                        className={classNames(
                          "text-4xl",
                          discountInfo?.percentOff
                            ? "font-bold line-through text-red-500"
                            : "font-extrabold text-gray-900"
                        )}
                      >
                        {bundle
                          ? renderBundlePrice(tier.id, tier.price)
                          : `₡${formatNumber(tier.price)}`}
                      </span>{" "}
                      {discountInfo?.percentOff ? (
                        <span className="text-4xl font-extrabold text-emerald-500">
                          ₡
                          {getSinglePurchasePrice(
                            tier.price,
                            discountInfo?.percentOff
                          )}
                        </span>
                      ) : null}
                      {/* <span className="text-base font-medium text-gray-500">/mo</span> */}
                    </p>
                    <button
                      id={tier.id}
                      className={classNames(
                        "mt-4 block w-full bg-orange-500 border-orange-500  border rounded-md py-2 text-sm font-semibold text-white text-center disabled:opacity-70",
                        !disabledBtn
                          ? "hover:bg-orange-600 hover:border-orange-600"
                          : ""
                      )}
                      onClick={onClick}
                      disabled={disabledBtn}
                    >
                      {btnLabel}
                    </button>
                  </div>
                )}
              </div>
              <div className="pt-6 pb-8 px-6">
                <h3 className="text-xs font-medium text-gray-900 tracking-wide uppercase">
                  Beneficios
                </h3>
                <ul role="list" className="mt-6 space-y-4">
                  {tier.includedFeatures.map((feature) => (
                    <li
                      key={feature.id}
                      className={classNames(
                        "flex space-x-3",
                        bundle && feature.hideForBundle ? "hidden" : ""
                      )}
                    >
                      {feature.positive ? (
                        <CheckIcon
                          className="flex-shrink-0 h-5 w-5 text-green-500"
                          aria-hidden="true"
                        />
                      ) : (
                        <MinusIcon
                          className="flex-shrink-0 h-5 w-5 text-red-500"
                          aria-hidden="true"
                        />
                      )}
                      <span className="text-sm text-gray-500">
                        {feature.content}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          );
        })}
    </div>
  );
}
